/**
 * TerminalConfigService
 *
 * Encapsula toda consulta à configuração local do terminal PDV Desktop.
 * Lê o banco SQLite via Electron IPC (window.electronAPI) e expõe
 * métodos simples para verificação de sessão e vínculo de terminal.
 *
 * Não realiza chamadas HTTP — exceto validarDispositivo() e verificarVinculoOnline().
 */
import { isDesktopMode, getElectronAPI } from "@/plugins/desktop";
import { SYSTEM_VARIABLE } from "@/modules/Application";

// ── Bloqueio de operações online ─────────────────────────────────────────────
// Flag módulo-level (não reativo ao Vue). Setado quando o backend confirma que
// este device_id não está mais autorizado. Limpo ao iniciar nova vinculação.
let _onlineBloqueado = false;

// ── Cache de validação do dispositivo (TTL: 5 minutos) ───────────────────────
// Evita chamar o backend a cada clique; operações críticas (NFC-e, sync)
// devem passar { forcar: true } para ignorar o cache.
const _CACHE_TTL_MS = 5 * 60 * 1000;
let _validacaoCache = { autorizado: null, motivo: null, code: null, ts: 0 };

const MENSAGEM_CONFIG_NFCE_AUSENTE =
  "Este terminal não possui configuração NFC-e. Você pode continuar utilizando o PDV para Pré-vendas. Configure a NFC-e no sistema para habilitar a emissão fiscal.";

const _CODES_BLOQUEIO_EMPRESA = new Set([
  "EMPRESA_BLOQUEADA",
  "ASSINATURA_VENCIDA",
  "EMPRESA_SEM_ACESSO",
  "PLANO_BLOQUEADO",
]);

const _extrairConfiguracaoNfce = (payload = {}) => {
  const data = payload.data ?? payload;
  return (
    data.configuracao_nfce ??
    data.nfce_config ??
    data.configuracaoNfce ??
    data.terminal?.configuracao_nfce ??
    null
  );
};

const _respostaDeclarouConfiguracaoNfce = (payload = {}) => {
  const data = payload.data ?? payload;
  return (
    Object.prototype.hasOwnProperty.call(data, "configuracao_nfce") ||
    Object.prototype.hasOwnProperty.call(data, "nfce_config") ||
    Object.prototype.hasOwnProperty.call(data, "configuracaoNfce") ||
    Object.prototype.hasOwnProperty.call(data.terminal || {}, "configuracao_nfce"
    )
  );
};

const _idConfiguracaoNfce = (config = {}) =>
  config.id_configuracao ??
  config.id ??
  config.id_configuracao_nfce ??
  config.configuracao_id ??
  null;

const _normalizarConfiguracaoNfce = (config = {}) => {
  const idTokenCSC =
    config.id_token_csc ||
    config.idTokenCSC ||
    config.idToken ||
    config.id_token ||
    null;

  const tokenCSC =
    config.token_csc ||
    config.tokenCSC ||
    config.csc ||
    config.CSC ||
    null;

  return {
    ...config,
    id_token_csc: idTokenCSC,
    token_csc: tokenCSC,
    // Mantém compatibilidade com fluxos legados que esperam camelCase.
    idTokenCSC,
    tokenCSC,
  };
};

const TerminalConfigService = {
  /** Marca este terminal como desvinculado — bloqueia operações online. */
  marcarDesvinculado() {
    _onlineBloqueado = true;
  },

  /** Remove o bloqueio online — chamado ao iniciar nova vinculação. */
  desbloquear() {
    _onlineBloqueado = false;
    _validacaoCache  = { autorizado: null, motivo: null, code: null, ts: 0 };
  },

  /** Retorna true se o terminal foi detectado como desvinculado pelo backend. */
  isOnlineBloqueado() {
    return _onlineBloqueado;
  },

  isCodigoBloqueioEmpresa(code) {
    return _CODES_BLOQUEIO_EMPRESA.has(String(code || "").trim().toUpperCase());
  },

  isCodigoSessaoEmpresaDivergente(code) {
    return (
      String(code || "")
        .trim()
        .toUpperCase() === "TERMINAL_EMPRESA_DIVERGENTE"
    );
  },

  isCodigoConfiguracaoNfceAusente(code) {
    return [
      "CONFIGURACAO_NFCE_AUSENTE",
      "CONFIGURACAO_NFCE_NAO_ENCONTRADA",
      "CONFIGURACAO_NFCE_TERMINAL_NAO_ENCONTRADA",
      "NFCE_CONFIG_NAO_ENCONTRADA",
    ].includes(String(code || "").trim().toUpperCase());
  },

  mensagemConfiguracaoNfceAusente() {
    return MENSAGEM_CONFIG_NFCE_AUSENTE;
  },

  async salvarConfiguracaoNfceTerminal(configuracao) {
    if (!isDesktopMode() || !configuracao || Object.keys(configuracao).length === 0) return false;
    const api = getElectronAPI();
    const configuracaoNormalizada = _normalizarConfiguracaoNfce(configuracao);

    await api.configuracaoNfce.salvarConfiguracao(configuracaoNormalizada);

    const idConfiguracao = _idConfiguracaoNfce(configuracaoNormalizada);
    const modelo = configuracaoNormalizada.modelo ?? 65;
    const ambiente = configuracaoNormalizada.ambiente ?? configuracaoNormalizada.tpAmb ?? null;
    const proximaNota =
      configuracaoNormalizada.proximaNota ??
      configuracaoNormalizada.proxima_nota ??
      configuracaoNormalizada.proximo_numero ??
      null;

    console.log("[FISCAL_CONFIG_SYNC]", {
      id_configuracao: configuracaoNormalizada.id_configuracao ?? configuracaoNormalizada.id ?? null,
      ambiente,
      modelo,
      serie: configuracaoNormalizada.serie ?? null,
      has_id_token_csc: !!configuracaoNormalizada.id_token_csc,
      has_token_csc: !!configuracaoNormalizada.token_csc,
    });

    await Promise.all([
      idConfiguracao ? api.config.set("id_configuracao", idConfiguracao) : Promise.resolve(),
      configuracaoNormalizada.serie ? api.config.set("serie_nfe", configuracaoNormalizada.serie) : Promise.resolve(),
      configuracaoNormalizada.id_empresa ? api.config.set("id_empresa", configuracaoNormalizada.id_empresa) : Promise.resolve(),
      ambiente != null ? api.config.set("ambiente_nfce", ambiente) : Promise.resolve(),
      modelo != null ? api.config.set("modelo_nfce", modelo) : Promise.resolve(),
      proximaNota != null ? api.config.set("proxima_nota", proximaNota) : Promise.resolve(),
    ]);
    return true;
  },

  async limparConfiguracaoNfceTerminal() {
    if (!isDesktopMode()) return false;
    const api = getElectronAPI();
    await api.configuracaoNfce.limparConfiguracao();
    if (api.certificado?.limparCache) {
      await api.certificado.limparCache();
    }
    return true;
  },

  async garantirConfiguracaoTerminalParaEmissao() {
    if (!isDesktopMode()) return;

    const api = getElectronAPI();
    const [
      uuidTerminal,
      deviceId,
      idEmpresa,
      idConfiguracao,
      configuracao] = await Promise.all([
      api.config.get("uuid_terminal"),
      api.config.get("device_id"),
      api.config.get("id_empresa"),
      api.config.get("id_configuracao"),
      api.configuracaoNfce.buscarConfiguracao(),
    ]);

    const modelo = Number(configuracao?.modelo ?? 65);
    const temNumeracaoValida =
      Number(configuracao?.proximaNota ?? 0) > 0 ||
      Number(configuracao?.proximaVenda ?? 0) > 0;

    const invalida =
      !uuidTerminal ||
      !deviceId ||
      !idEmpresa ||
      !idConfiguracao ||
      !configuracao?.id_configuracao ||
      !configuracao?.ambiente ||
      modelo !== 65 ||
      !configuracao?.serie ||
      !temNumeracaoValida;

    if (invalida) {
      const err = new Error(
        "Configuração fiscal do terminal incompleta. Reconfigure o terminal para emitir NFC-e."
      );
      err.code = "TERMINAL_CONFIG_FISCAL_INVALIDA";
      throw err;
    }
  },

  async garantirConfiguracaoNfceLocal() {
    if (!isDesktopMode()) return;
    const api = getElectronAPI();
    const configuracao = await api.configuracaoNfce.buscarConfiguracao();
    if (!configuracao || !configuracao.id_configuracao) {
      const err = new Error(MENSAGEM_CONFIG_NFCE_AUSENTE);
      err.code = "CONFIGURACAO_NFCE_AUSENTE";
      throw err;
    }
  },

  async obterBloqueioLocal() {
    if (!isDesktopMode()) {
      return { bloqueado: false, code: null, motivo: "" };
    }

    try {
      const api = getElectronAPI();
      const [bloqueadoRaw, code, motivo] = await Promise.all([
        api.config.get("terminal_bloqueado"),
        api.config.get("terminal_code_bloqueio"),
        api.config.get("terminal_motivo_bloqueio"),
      ]);

      const bloqueado =
        bloqueadoRaw === "1" ||
        bloqueadoRaw === 1 ||
        bloqueadoRaw === true ||
        bloqueadoRaw === "true";

      return {
        bloqueado,
        code: code || null,
        motivo: motivo || "",
      };
    } catch (_) {
      return { bloqueado: false, code: null, motivo: "" };
    }
  },

  /**
   * Verifica se o terminal já foi vinculado e validado neste dispositivo.
   *
   * @returns {Promise<{ configurado: boolean, terminal: object|null }>}
   */
  async verificarConfiguracaoLocal() {
    if (!isDesktopMode()) return { configurado: false, terminal: null };
    try {
      const terminal = await getElectronAPI().terminal.getInfo();
      return {
        configurado: !!(terminal && terminal.validado),
        terminal: terminal || null,
      };
    } catch (_) {
      return { configurado: false, terminal: null };
    }
  },

  /**
   * Indica se existe um token de sessão salvo localmente.
   * Não valida o token no servidor — apenas verifica presença.
   *
   * @returns {Promise<boolean>}
   */
  async temSessaoSalva() {
    if (!isDesktopMode()) return false;
    try {
      const token = await getElectronAPI().config.get("api_token");
      return !!token;
    } catch (_) {
      return false;
    }
  },

  /**
   * Retorna o e-mail do usuário salvo localmente (último login bem-sucedido).
   *
   * @returns {Promise<string>}
   */
  async getEmailSalvo() {
    if (!isDesktopMode()) return "";
    try {
      return (await getElectronAPI().config.get("usuario_email")) || "";
    } catch (_) {
      return "";
    }
  },

  /**
   * Persiste o estado de bloqueio do terminal localmente no app_config.
   * Chamado quando o backend confirma que o terminal foi desvinculado.
   *
   * Campos gravados:
   *   terminal_status_validacao, terminal_bloqueado,
   *   terminal_motivo_bloqueio, terminal_code_bloqueio, terminal_validado_em
   */
  async marcarBloqueadoLocalmente({ code = "TERMINAL_DESVINCULADO", motivo = "",
  } = {}) {
    _onlineBloqueado = true;
    if (!isDesktopMode()) return;
    try {
      const api = getElectronAPI();
      await Promise.all([
        api.config.set("terminal_status_validacao", "BLOQUEADO"),
        api.config.set("terminal_bloqueado", "1"),
        api.config.set("terminal_motivo_bloqueio", motivo),
        api.config.set("terminal_code_bloqueio", code),
        api.config.set("terminal_validado_em", new Date().toISOString()),
      ]);
    } catch (e) {
      console.warn("[TerminalConfigService] marcarBloqueadoLocalmente:", e.message);
    }
  },

  /** Grava a data da última validação bem-sucedida no app_config. */
  async _salvarUltimoAcesso() {
    if (!isDesktopMode()) return;
    try {
      const api   = getElectronAPI();
      const agora = new Date().toISOString();
      await Promise.all([
        api.config.set("terminal_status_validacao", "AUTORIZADO"),
        api.config.set("terminal_bloqueado", "0"),
        api.config.set("terminal_motivo_bloqueio", ""),
        api.config.set("terminal_code_bloqueio", ""),
        api.config.set("terminal_validado_em", agora),
        api.config.set("terminal_ultimo_acesso_em", agora),
      ]);
    } catch (e) {
      console.warn("[TerminalConfigService] _salvarUltimoAcesso:", e.message);
    }
  },

  /**
   * Valida o dispositivo no backend via POST.
   *
   * Endpoint: POST /api/offline/terminal/validar-dispositivo
   *   Body:   { uuid_terminal, device_id }
   *   Header: Authorization Bearer <token>
   *
   * Política de falha:
   *   - Erro de rede / timeout → fail-open   (autorizado: true)
   *   - HTTP 5xx              → fail-open   (autorizado: true)
   *   - HTTP 401/403          → fail-closed (autorizado: false)
   *   - JSON success === false → fail-closed (autorizado: false)
   *
   * @returns {Promise<{ autorizado: boolean, motivo?: string, code?: string }>}
   */
  async validarDispositivo() {
    if (!isDesktopMode()) return { autorizado: true };
    if (!navigator.onLine)  return { autorizado: true };

    try {
      const api = getElectronAPI();

      const [terminal, token, apiUrlSaved, deviceId] = await Promise.all([
        api.terminal.getInfo(),
        api.config.get("api_token"),
        api.config.get("api_url"),
        api.device.getDeviceId(),
      ]);

      // Sem configuração local → deixa o guard de rota lidar
      if (!terminal || !terminal.uuid_terminal || !token) {
        return { autorizado: true };
      }

      if (!deviceId) {
        return {
          autorizado: false,
          code: "TERMINAL_CONFIG_INVALIDA",
          motivo: "Terminal não configurado corretamente. Configure novamente o terminal.",
        };
      }

      const baseUrl = (apiUrlSaved || SYSTEM_VARIABLE.BASE_URL_API || "")
        .replace(/\/$/, "");

      let resp;
      try {
        resp = await fetch(`${baseUrl}/api/offline/terminal/validar-dispositivo`, {
          method:  "POST",
          headers: {
            Authorization:  `Bearer ${token}`,
            Accept:         "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            uuid_terminal: terminal.uuid_terminal,
            device_id:     deviceId || "",
          }),
          signal: AbortSignal.timeout(8000),
        });
      } catch (_netErr) {
        // Erro de rede (timeout, sem conexão) → fail-open
        return { autorizado: true };
      }

      // Erros de servidor genéricos (5xx) → fail-open
      if (resp.status >= 500) return { autorizado: true };

      const json = await resp.json().catch(() => ({}));

      // HTTP 401/403 explícito → desautorizado
      if (resp.status === 401 || resp.status === 403) {
        const motivo = json.message ?? json.msg ?? "Terminal desvinculado ou não autorizado.";
        return { autorizado: false, code: json.code ?? "TERMINAL_NAO_AUTORIZADO", motivo,
        };
      }

      // Ausência de configuração fiscal não desautoriza o terminal operacional.
      // Este tratamento também mantém compatibilidade com versões antigas do back.
      const isNaoAutorizado = json.status === false || json.success === false;
      if (isNaoAutorizado) {
        const code   = json.code    ?? "TERMINAL_DESVINCULADO";
        if (this.isCodigoConfiguracaoNfceAusente(code)) {
          await this.limparConfiguracaoNfceTerminal();
          return { autorizado: true, nfceDisponivel: false };
        }
        return {
          autorizado: false,
          code,
          motivo: json.message ?? json.msg ?? "Terminal não autorizado.",
        };
      }

      if (!resp.ok) {
        const err = new Error(
          json.message ??
            json.msg ??
            `Erro ao validar terminal (HTTP ${resp.status}).`
        );
        err.code = json.code ?? "ERRO_VALIDACAO_TERMINAL";
        throw err;
      }

      // Divergência de device_id: terminal vinculado a outro computador
      if (json.device_id && deviceId && json.device_id !== deviceId) {
        return {
          autorizado: false,
          code:   "TERMINAL_VINCULADO_OUTRO_DEVICE",
          motivo: "Este terminal está vinculado a outro computador.",
        };
      }

      const configuracaoNfce = _extrairConfiguracaoNfce(json);
      if (configuracaoNfce) {
        await this.salvarConfiguracaoNfceTerminal({
          ...configuracaoNfce,
          payload_json: json,
        });
      } else if (_respostaDeclarouConfiguracaoNfce(json)) {
        await this.limparConfiguracaoNfceTerminal();
      }

      return { autorizado: true, nfceDisponivel: !!configuracaoNfce };

    } catch (e) {
      console.warn("[TerminalConfigService] validarDispositivo:", e.message);
      throw e;
    }
  },

  /**
   * Garante que o terminal está autorizado antes de uma operação sensível.
   *
   * Comportamento:
   *   - Offline          → retorna sem erro (operação local permitida).
   *   - Cache válido (<5 min) e forcar=false → usa cache.
   *   - Cache expirado ou forcar=true → valida com backend.
   *   - Falha de vínculo → persiste bloqueio localmente e lança Error com .code.
   *
   * @param {{ forcar?: boolean }} options
   *   forcar: true = sempre valida com backend, ignorando cache (usar em NFC-e, sync).
   *
   * @throws {Error} com err.code quando terminal não está autorizado.
   */
  async garantirTerminalAutorizado({ forcar = false } = {}) {
    if (!isDesktopMode()) return;

    // Bloqueio de empresa já conhecido localmente: fail-closed, inclusive offline.
    const bloqueioLocal = await this.obterBloqueioLocal();
    if (bloqueioLocal.bloqueado && this.isCodigoBloqueioEmpresa(bloqueioLocal.code)) {
      _onlineBloqueado = true;
      const err = new Error(
        bloqueioLocal.motivo ||
        "Esta empresa está bloqueada no PDV. Entre em contato com o suporte para regularização."
      );
      err.code = bloqueioLocal.code;
      throw err;
    }

    const agora       = Date.now();
    const cacheValido = !forcar
      && _validacaoCache.autorizado !== null
      &&
      agora - _validacaoCache.ts < _CACHE_TTL_MS;

    if (cacheValido) {
      if (!_validacaoCache.autorizado) {
        const err = new Error(_validacaoCache.motivo || "Terminal não autorizado.");
        err.code  = _validacaoCache.code || "TERMINAL_DESVINCULADO";
        throw err;
      }
      return; // cache confirma autorizado
    }

    if (!navigator.onLine) {
      return; // offline → permite operar localmente
    }

    // Valida com backend
    const resultado = await this.validarDispositivo();

    // Atualiza cache
    _validacaoCache = {
      autorizado: resultado.autorizado,
      motivo:     resultado.motivo ?? null,
      code:       resultado.code   ?? null,
      ts:         agora,
    };

    if (!resultado.autorizado) {
      await this.marcarBloqueadoLocalmente({
        code:   resultado.code   ?? "TERMINAL_DESVINCULADO",
        motivo: resultado.motivo ?? "Terminal não autorizado.",
      });
      const err = new Error(
        resultado.motivo ||
        "Este terminal foi desvinculado no painel web. Configure novamente o terminal para continuar."
      );
      err.code = resultado.code || "TERMINAL_DESVINCULADO";
      throw err;
    }

    // Autorizado: persiste último acesso
    await this._salvarUltimoAcesso();
  },

  /**
   * Consulta o backend para verificar se o device_id ainda está autorizado.
   * Mantido para compatibilidade — delega para validarDispositivo().
   *
   * @returns {Promise<{ autorizado: boolean, motivo?: string }>}
   */
  async verificarVinculoOnline() {
    return this.validarDispositivo();
  },
};

export default TerminalConfigService;
